import { useCallback, useEffect, useMemo } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/modules/auth/hooks/useAuth';
import { PageHero } from '@/shared/components/dashboard/PageHero';
import { EmptyPanel, ErrorPanel } from '@/shared/components/dashboard/panels';
import { Button } from '@/shared/components/ui/button';
import { Icons } from '@/shared/components/ui/icons';
import { isApiError } from '@/shared/lib/apiClient';
import { ActiveSessionsTable } from '../components/ActiveSessionsTable';
import { ActivitySummaryCards, ActivitySummarySkeleton } from '../components/ActivitySummaryCards';
import {
  PlatformUsageOverview,
  PlatformUsageOverviewSkeleton,
} from '../components/PlatformUsageOverview';
import { RecentActivityFeed } from '../components/RecentActivityFeed';
import { TopModulesPanel, TopPagesPanel } from '../components/TopRankedPanels';
import { usePlatformUsageLive } from '../hooks/usePlatformUsageLive';
import { usePlatformUsageRecent } from '../hooks/usePlatformUsageRecent';
import { usePlatformUsageSummary } from '../hooks/usePlatformUsageSummary';
import { computeLiveMetrics } from '../utils/liveMetrics';

function resolveQueryErrorMessage(error: unknown, t: (key: string) => string): string {
  if (isApiError(error)) {
    if (error.status === 403) return t('platformUsage.errors.forbidden');
    if (error.status === 401) return t('platformUsage.errors.unauthorized');
  }
  return t('platformUsage.errors.loadFailed');
}

export function PlatformUsageMonitoringPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();

  const isSuperAdmin = user?.roles.includes('super_admin') ?? false;
  const canReadAnalytics = user?.permissions.includes('analytics.read') ?? false;
  const canAccess = isSuperAdmin && canReadAnalytics;
  const locale = i18n.language === 'ar' ? 'ar-SA' : 'en-US';

  useEffect(() => {
    if (user && !isSuperAdmin) {
      void navigate({ to: '/dashboard', replace: true });
    }
  }, [user, isSuperAdmin, navigate]);

  const liveQuery = usePlatformUsageLive(canAccess);
  const recentQuery = usePlatformUsageRecent(canAccess);
  const summaryQuery = usePlatformUsageSummary(canAccess);

  const refetchAll = useCallback(() => {
    void liveQuery.refetch();
    void recentQuery.refetch();
    void summaryQuery.refetch();
  }, [liveQuery, recentQuery, summaryQuery]);

  const isInitialLoading =
    canAccess &&
    (liveQuery.isLoading || recentQuery.isLoading || summaryQuery.isLoading) &&
    !liveQuery.data &&
    !recentQuery.data &&
    !summaryQuery.data;

  const isFetching = liveQuery.isFetching || recentQuery.isFetching || summaryQuery.isFetching;

  const liveMetrics = useMemo(
    () => computeLiveMetrics(liveQuery.data?.sessions ?? []),
    [liveQuery.data?.sessions],
  );

  const lastUpdatedAt = useMemo(() => {
    const timestamps = [
      liveQuery.dataUpdatedAt,
      recentQuery.dataUpdatedAt,
      summaryQuery.dataUpdatedAt,
    ].filter((value): value is number => value > 0);
    if (timestamps.length === 0) return null;
    return new Date(Math.max(...timestamps)).toISOString();
  }, [liveQuery.dataUpdatedAt, recentQuery.dataUpdatedAt, summaryQuery.dataUpdatedAt]);

  const primaryError = liveQuery.error ?? recentQuery.error ?? summaryQuery.error;
  const hasBlockingError =
    canAccess &&
    !isInitialLoading &&
    liveQuery.isError &&
    recentQuery.isError &&
    summaryQuery.isError;

  if (!user || !isSuperAdmin) {
    return null;
  }

  if (!canReadAnalytics) {
    return (
      <div className="space-y-6">
        <PageHero
          title={t('platformUsage.title')}
          subtitle={t('platformUsage.subtitle')}
          icon={<Icons.BarChart className="h-7 w-7" aria-hidden />}
        />
        <EmptyPanel
          title={t('platformUsage.errors.forbidden')}
          message={t('platformUsage.errors.missingPermission')}
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHero
        title={t('platformUsage.title')}
        subtitle={t('platformUsage.subtitle')}
        icon={<Icons.BarChart className="h-7 w-7" aria-hidden />}
        actions={
          <>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={refetchAll}
              disabled={isFetching}
            >
              <Icons.RefreshCw
                className={isFetching ? 'h-4 w-4 animate-spin' : 'h-4 w-4'}
                aria-hidden
              />
              <span className="ms-2">{t('common.refresh')}</span>
            </Button>
            <span className="text-xs text-muted-foreground self-center">
              {t('platformUsage.autoRefreshHint')}
            </span>
          </>
        }
      />

      {hasBlockingError ? (
        <div className="space-y-3">
          <ErrorPanel message={resolveQueryErrorMessage(primaryError, t)} />
          <div className="flex justify-center">
            <Button type="button" variant="outline" size="sm" onClick={refetchAll}>
              {t('common.retry')}
            </Button>
          </div>
        </div>
      ) : (
        <>
          {isInitialLoading ? (
            <PlatformUsageOverviewSkeleton />
          ) : liveQuery.isError ? (
            <ErrorPanel message={resolveQueryErrorMessage(liveQuery.error, t)} />
          ) : (
            <PlatformUsageOverview
              metrics={liveMetrics}
              lastUpdatedAt={lastUpdatedAt}
              locale={locale}
            />
          )}

          <ActiveSessionsTable
            sessions={liveQuery.data?.sessions ?? []}
            locale={locale}
            isLoading={liveQuery.isLoading && !liveQuery.data}
            isError={liveQuery.isError}
          />

          <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
            <RecentActivityFeed
              events={recentQuery.data ?? []}
              locale={locale}
              isLoading={recentQuery.isLoading && !recentQuery.data}
              isError={recentQuery.isError}
            />

            {summaryQuery.isLoading && !summaryQuery.data ? (
              <ActivitySummarySkeleton />
            ) : summaryQuery.isError ? (
              <ErrorPanel message={resolveQueryErrorMessage(summaryQuery.error, t)} />
            ) : summaryQuery.data ? (
              <ActivitySummaryCards summary={summaryQuery.data} />
            ) : null}
          </div>

          {summaryQuery.data ? (
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
              <TopModulesPanel topModules={summaryQuery.data.topModules} />
              <TopPagesPanel topPages={summaryQuery.data.topPages} />
            </div>
          ) : summaryQuery.isLoading ? (
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
              <div className="h-64 rounded-xl border border-border bg-card animate-pulse" />
              <div className="h-64 rounded-xl border border-border bg-card animate-pulse" />
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}
